import { objectEntries } from "app/helpers/objects";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { CrmRecordsRequestDto } from "app/pages/sales-intelligence/domain/crm-integration/types/records";
import type { ILeadsSearchFiltersDto } from "app/pages/sales-intelligence/modules/leads-search-filters";
import type SignalId from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalId";
import type { PaginatedTableRequest } from "app/pages/sales-intelligence/types/table";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { DEFAULT_PAGE } from "../../constants/dto";
import type {
    LeadDetailedResultsRequestDto,
    LeadsExcelDownloadRequestDto,
    LeadsSearchRequestDto,
    LeadsSearchRequestDtoByType,
    LeadsSearchRow,
} from "../../types";
import { getLeadDomainFromRow } from "../../utils/rows";
import type { LeadsTabsState } from "../types";

type LeadsSearchRequestDtoFactoryParams = {
    searchText: string;
    contactsSearchText: string;
    appsSearchText: string;
    isNewOnly?: boolean;
} & LeadsSearchRequestDto;

export const createLeadsSearchRequestDto = ({
    asc,
    orderBy,
    page,
    pageSize,
    searchText,
    contactsSearchText,
    appsSearchText,
    isNewOnly,
    queryFilters = {},
    leadGenTrafficAggregationEnabled,
}: LeadsSearchRequestDtoFactoryParams): LeadsSearchRequestDto => {
    const queryFiltersCopy = { ...queryFilters };

    if (queryFilters.leadsFilters || searchText) {
        queryFiltersCopy.leadsFilters = { ...queryFilters.leadsFilters, searchText: searchText };
    }
    if (queryFilters.contactsFilters || contactsSearchText) {
        queryFiltersCopy.contactsFilters = { ...queryFilters.contactsFilters, searchText: contactsSearchText };
    }
    if (queryFilters.appsFilters || appsSearchText) {
        queryFiltersCopy.appsFilters = {
            ...queryFilters.appsFilters,
            ...(appsSearchText ? { searchText: appsSearchText } : {}),
        };
    }

    return {
        asc,
        orderBy,
        page,
        pageSize,
        isNewOnly,
        leadGenTrafficAggregationEnabled,
        queryFilters: queryFiltersCopy,
    };
};

export const createSearchResultsDtoByLeadsType = (
    tabsState: LeadsTabsState,
    searchText: string,
    contactsSearchText: string,
    isNewOnly: boolean,
    queryFilters: Partial<ILeadsSearchFiltersDto>,
    leadGenTrafficAggregationEnabled: boolean,
    appsSearchText: string,
): Readonly<LeadsSearchRequestDtoByType> => {
    return objectEntries(tabsState).reduce<LeadsSearchRequestDtoByType>(
        (result, [leadType, tabState]) => {
            result[leadType] = createLeadsSearchRequestDto({
                queryFilters,
                ...tabState,
                leadGenTrafficAggregationEnabled,
                contactsSearchText,
                appsSearchText,
                searchText,
                isNewOnly,
            });

            return result;
        },
        { websites: {} as LeadsSearchRequestDto, companies: {} as LeadsSearchRequestDto },
    );
};

// Refer to createDomainsSearchResultsRequestDto implementation
export const createLeadDomainsSearchRequestDto = ({
    asc,
    orderBy,
    page,
    pageSize,
    leadGenTrafficAggregationEnabled,
    queryFilters,
}: LeadsSearchRequestDto): LeadsSearchRequestDto => ({
    asc,
    orderBy,
    page,
    pageSize,
    leadGenTrafficAggregationEnabled,
    queryFilters: {
        leadsFilters: { countries: queryFilters.leadsFilters?.countries },
        // TODO add signals
    },
});

export const createTopNLeadsSearchRequestDto = (
    searchRequestDto: Readonly<LeadsSearchRequestDto>,
    leadsCount: number,
): LeadsSearchRequestDto => {
    return {
        ...searchRequestDto,
        isLeadColumnOnly: true,
        page: DEFAULT_PAGE,
        pageSize: leadsCount,
    };
};

export const createDetailedResultsRequestDto = <R extends LeadsSearchRow>(
    requestDto: LeadsSearchRequestDto,
    rows: readonly R[],
): LeadDetailedResultsRequestDto => {
    return {
        domains: rows.map(getLeadDomainFromRow),
        countries: requestDto.queryFilters.leadsFilters?.countries as readonly number[],
    };
};

export const createCrmRecordsRequestDto = (
    expandedRowState: PaginatedTableRequest,
    filters: Partial<ILeadsSearchFiltersDto>,
    provider: CrmIntegrationProvider,
): CrmRecordsRequestDto =>
    // `salesforceCustomFilters` is an array (SI-8738) — guard on length, since `[]` is truthy but carries nothing.
    provider === "salesforce" && filters.salesforceCustomFilters?.length
        ? { ...expandedRowState, queryFilters: { salesforceCustomFilters: filters.salesforceCustomFilters } }
        : expandedRowState;

export const createExcelExportRequestDto = (
    searchRequestDto: LeadsSearchRequestDto,
    signalNamesByIds: { [key: SignalId]: string },
    domainsOrCount: string[] | number,
): LeadsExcelDownloadRequestDto => {
    const { includeDomains, top }: Pick<LeadsExcelDownloadRequestDto, "includeDomains" | "top"> = is.number(
        domainsOrCount,
    )
        ? { top: domainsOrCount, includeDomains: [] }
        : { top: undefined, includeDomains: domainsOrCount };

    return {
        top,
        includeDomains,
        signalNamesByIds,
        ...searchRequestDto,
    };
};
